import { removeItemToast, selectedToasts, type Toast } from '@shared-core/features/notifications'
import { useNotifications } from '@shared-core/features/notifications/context'
import { useI18N } from '@shared-core/hooks/use-i18n'
import knownErrors from '@shared-core/locales/known-errors.dynamic.i18n.json'
import { useAppDispatch, useAppSelector } from '@shared-core/store'
import type { AppIconSource } from '@shared-ui/assets/icons'
import { ActiveTouchAction } from '@shared-ui/components/ui/active-touch-action'
import { AppIcon } from '@shared-ui/components/ui/app-icon'
import { GText } from '@shared-ui/components/ui/text'
import { rw, useAppTheme } from '@shared-ui/theme'
import { memo, useCallback, useEffect, useLayoutEffect } from 'react'
import { Platform, View } from 'react-native'
import Animated, {
  cancelAnimation,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
} from 'react-native-reanimated'
import { FullWindowOverlay } from 'react-native-screens'
import { useCreateStyles } from './styles'

type ToastElementProps = {
  toast: Toast
}

function getToastIcon(toast: Toast, styles: ReturnType<typeof useCreateStyles>) {
  if (toast.icon) {
    return <AppIcon icon={toast.icon as AppIconSource} size={rw(24)} style={styles.appIcon} />
  }

  if (toast.type === 'error') {
    return <AppIcon icon="status-error" size={rw(20)} style={[styles.appIcon, styles.appIconError]} />
  }

  if (toast.type === 'success') {
    return <AppIcon icon="status-success" size={rw(20)} style={[styles.appIcon, styles.appIconSuccess]} />
  }

  if (toast.type === 'info') {
    return <AppIcon icon="info" size={rw(20)} style={[styles.appIcon, styles.appIconInfo]} />
  }

  return null
}

type TranslateKnownError = (key: keyof (typeof knownErrors)['ru'], variables?: { value: string }) => string

function getToastText(toast: Toast, t: TranslateKnownError): string {
  if (typeof toast.text === 'string') {
    return toast.text
  }

  const errorKey = toast.text.error as Parameters<TranslateKnownError>[0]
  // Без variables processI18N не вызывает .replace и на неизвестном ключе просто отдаёт пусто.
  const template = t(errorKey)

  if (!template) {
    return t('SOMETHING_WENT_WRONG')
  }

  if (!toast.text.value) {
    return template
  }

  return t(errorKey, { value: toast.text.value })
}

const ToastElement = memo(function ToastElementComponent({ toast }: ToastElementProps) {
  const { t } = useI18N(knownErrors)
  const styles = useCreateStyles()
  const theme = useAppTheme()
  const dispatch = useAppDispatch()
  const { removeToast } = useNotifications()
  const anim = useSharedValue(0)
  const onClose = useCallback(() => removeToast(toast.uuid), [])

  // анимация открытия закрытия
  useEffect(() => {
    let timer = null

    anim.value = withSpring(!toast.isHidden ? 1 : 0, {
      mass: 1,
      damping: 20,
      stiffness: 180,
    })

    if (toast.isHidden) {
      timer = setTimeout(() => dispatch(removeItemToast(toast.uuid)), 500)
    }

    return () => {
      cancelAnimation(anim)

      if (timer) {
        clearTimeout(timer)
      }
    }
  }, [toast.isHidden])

  useLayoutEffect(() => {
    let timer = null

    if (toast.timeout) {
      timer = setTimeout(() => removeToast(toast.uuid), toast.timeout)
    }

    return () => {
      if (timer) {
        clearTimeout(timer)
      }
    }
  }, [toast.uuid])

  const styleAnim = useAnimatedStyle(() => ({
    opacity: anim.get(),
    transform: [
      { translateY: interpolate(anim.get(), [0, 1], [-40, 0]) },
      { scale: interpolate(anim.get(), [0, 1], [0.94, 1]) },
    ],
  }))

  const text = getToastText(toast, t)
  const icon = getToastIcon(toast, styles)

  const isClosable = toast.isClosable !== false

  const body = (
    <>
      {icon ? <View style={styles.side}>{icon}</View> : null}
      <GText variant="caption-14-medium" color={theme.COLOR_TEXT_INVERSE_PRIMARY} style={styles.text}>
        {text}
      </GText>
    </>
  )

  if (isClosable) {
    return (
      <Animated.View style={[styles.animPos, styleAnim]}>
        <View style={styles.animInner}>
          <ActiveTouchAction
            key={toast.uuid}
            style={[styles.block, Platform.OS === 'web' && styles.blockWeb]}
            onPress={onClose}
          >
            {body}
          </ActiveTouchAction>
        </View>
      </Animated.View>
    )
  }

  return (
    <Animated.View key={toast.uuid} style={[styles.animPos, styleAnim, { pointerEvents: 'box-only' }]}>
      <View style={[styles.animInner, styles.block]}>{body}</View>
    </Animated.View>
  )
})

export const ServiceNotification = memo(function ServiceNotificationFn({ isRoot }: { isRoot?: boolean }) {
  const toasts = useAppSelector(selectedToasts)
  const styles = useCreateStyles()

  const body = (
    <View style={styles.wrapperTop}>
      {toasts.map((toast) => (
        <ToastElement toast={toast} key={toast.uuid} />
      ))}
    </View>
  )

  return Platform.OS === 'ios' && isRoot ? <FullWindowOverlay>{body}</FullWindowOverlay> : body
})
